import React, { useCallback, useEffect, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Heading,
  HStack,
  Input,
  Stack,
  Table,
  Text,
  VStack,
  Badge,
  useDisclosure,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Tbody, Td, Th, Thead, Tr } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import AppLayout from './AppLayout';
import { bandasAPI } from '../services/api';
import { useProductTour } from '../hooks/useProductTour';
import { bandaPermisosSteps } from '../tours/bandaPermisosTour';

const PERM_KEYS = [
  ['permisoRepertorio', 'Archivo musical'],
  ['permisoMusicos', 'Mis músicos'],
  ['permisoFormaciones', 'Formaciones'],
  ['permisoActuaciones', 'Actuaciones'],
  ['permisoEnsayos', 'Ensayos'],
  ['permisoDocumentos', 'Documentos'],
  ['permisoContactos', 'Contactos'],
  ['permisoEstadisticas', 'Estadísticas'],
  ['permisoObjetivos', 'Objetivos'],
  ['permisoEncuestas', 'Encuestas'],
  ['permisoTuWeb', 'Tu web (completo)'],
];

const emptyForm = {
  email: '',
  password: '',
  nombreVisible: '',
  activo: true,
  permisoRepertorio: false,
  permisoEnsayos: false,
  permisoActuaciones: false,
  permisoMusicos: false,
  permisoFormaciones: false,
  permisoDocumentos: false,
  permisoContactos: false,
  permisoEstadisticas: false,
  permisoObjetivos: false,
  permisoEncuestas: false,
  permisoTuWeb: false,
};

export default function BandaPermisosGestion() {
  
  useProductTour('banda-permisos', bandaPermisosSteps, { autoStart: false });
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState(emptyForm);
  const [editingUserId, setEditingUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bandasAPI.listColaboradoresGestion();
      setItems(data.items || []);
    } catch (e) {
      toaster.create({
        title: 'No se pudieron cargar los usuarios',
        description: e.response?.data?.message || e.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingUserId(null);
    setForm(emptyForm);
    onOpen();
  };

  const openEdit = (row) => {
    setEditingUserId(row.userId);
    setForm({
      email: row.email,
      password: '',
      nombreVisible: row.nombreVisible || '',
      activo: row.activo,
      permisoRepertorio: row.permisoRepertorio,
      permisoEnsayos: row.permisoEnsayos,
      permisoActuaciones: row.permisoActuaciones,
      permisoMusicos: row.permisoMusicos,
      permisoFormaciones: row.permisoFormaciones,
      permisoDocumentos: row.permisoDocumentos,
      permisoContactos: row.permisoContactos,
      permisoEstadisticas: row.permisoEstadisticas,
      permisoTuWeb: row.permisoTuWeb,
    });
    onOpen();
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editingUserId) {
        const body = {
          nombreVisible: form.nombreVisible || null,
          activo: form.activo,
        };
        for (const [k] of PERM_KEYS) {
          body[k] = form[k];
        }
        if (form.password?.trim().length >= 8) {
          body.newPassword = form.password;
        }
        await bandasAPI.updateColaboradorGestion(editingUserId, body);
        toaster.create({ title: 'Usuario actualizado', type: 'success' });
      } else {
        await bandasAPI.createColaboradorGestion({
          email: form.email.trim(),
          password: form.password,
          nombreVisible: form.nombreVisible?.trim() || undefined,
          activo: form.activo,
          permisoRepertorio: form.permisoRepertorio,
          permisoEnsayos: form.permisoEnsayos,
          permisoActuaciones: form.permisoActuaciones,
          permisoMusicos: form.permisoMusicos,
          permisoFormaciones: form.permisoFormaciones,
          permisoDocumentos: form.permisoDocumentos,
          permisoContactos: form.permisoContactos,
          permisoEstadisticas: form.permisoEstadisticas,
          permisoTuWeb: form.permisoTuWeb,
        });
        toaster.create({ title: 'Usuario creado', type: 'success' });
      }
      onClose();
      await load();
    } catch (e) {
      toaster.create({
        title: 'Error',
        description: e.response?.data?.message || e.message,
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout userType="banda">
      <Box bg={bg} py={{ base: 4, md: 8 }} minH="100vh">
        <Container maxW="6xl">
          <VStack align="stretch" gap={6}>
            <VStack align="start" gap={1} data-tour="permisos-header">
              <Heading size="lg">Permisos de gestión</Heading>
              <Text color="gray.600" fontSize="sm">
                Crea usuarios con email y contraseña para directores externos o miembros de la directiva. Solo el
                titular de la cuenta ve esta sección. Los delegados solo acceden a las pestañas que marques (y a Tu web
                si lo activas).
              </Text>
            </VStack>

            <Box bg={cardBg} borderRadius="md" p={4} shadow="sm">
              <HStack justify="space-between" mb={4}>
                <Text fontWeight="600">Usuarios delegados</Text>
                <Button colorPalette="purple" size="sm" onClick={openCreate} data-tour="permisos-add">
                  Añadir usuario
                </Button>
              </HStack>
              {loading ? (
                <Text>Cargando…</Text>
              ) : (
                <Box overflowX="auto" data-tour="permisos-tabla">
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Email</Table.ColumnHeader>
                        <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                        <Table.ColumnHeader>Estado</Table.ColumnHeader>
                        <Table.ColumnHeader />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {items.map((row) => (
                        <Table.Row key={row.id}>
                          <Table.Cell>{row.email}</Table.Cell>
                          <Table.Cell>{row.nombreVisible || '—'}</Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={row.activo && row.cuentaActiva ? 'green' : 'gray'}>
                              {row.activo && row.cuentaActiva ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Button size="xs" variant="outline" onClick={() => openEdit(row)}>
                              Editar
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                  {items.length === 0 && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Aún no hay usuarios delegados.
                    </Text>
                  )}
                </Box>
              )}
            </Box>
          </VStack>
        </Container>
      </Box>

      <Dialog.Root open={isOpen} size='xl' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{editingUserId ? 'Editar delegado' : 'Nuevo delegado'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Stack gap={4}>
                  {!editingUserId && (
                    <>
                      <Field.Root required>
                        <Field.Label>Email</Field.Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        />
                      </Field.Root>
                      <Field.Root required>
                        <Field.Label>Contraseña</Field.Label>
                        <Input
                          type="password"
                          value={form.password}
                          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Mínimo 8 caracteres, mayúscula, minúscula, número y símbolo.
                        </Text>
                      </Field.Root>
                    </>
                  )}
                  {editingUserId && (
                    <Field.Root>
                      <Field.Label>Nueva contraseña (opcional)</Field.Label>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="Dejar vacío para no cambiar"
                      />
                    </Field.Root>
                  )}
                  <Field.Root>
                    <Field.Label>Nombre visible (opcional)</Field.Label>
                    <Input
                      value={form.nombreVisible}
                      onChange={(e) => setForm((f) => ({ ...f, nombreVisible: e.target.value }))}
                      placeholder="Ej. Director musical"
                    />
                  </Field.Root>
                  <Checkbox.Root
                    onCheckedChange={(details) => setForm((f) => ({ ...f, activo: details.checked === true }))}
                    checked={form.activo}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Acceso activo
                                    </Checkbox.Label></Checkbox.Root>
                  <Separator />
                  <Text fontWeight="600" fontSize="sm">
                    Permisos de Gestión
                  </Text>
                  <IsolateCheckboxesFromField>
                  <Stack gap={2}>
                    {PERM_KEYS.map(([key, label]) => (
                      <Checkbox.Root
                        key={key}
                        onCheckedChange={(details) => setForm((f) => ({ ...f, [key]: details.checked === true }))}
                        checked={form[key]}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        {label}
                      </Checkbox.Label></Checkbox.Root>
                    ))}
                  </Stack>
                  </IsolateCheckboxesFromField>
                </Stack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={submit} loading={saving}>
                  Guardar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
}
